import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Plus, Pencil, Trash2, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { hub_name: '', area_name: '', aliases: '', region: '', publication_id: '' };

export default function LocalAreasPage() {
  const { user }  = useAuth();
  const [pubs, setPubs]           = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedPub, setSelectedPub] = useState('');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [expanded, setExpanded]   = useState({});

  const canManage = ['admin', 'editor'].includes(user?.role);

  const fetchPubs = async () => {
    try {
      const res = await api.get('/publications');
      // Only geo publications
      const geo = res.data.filter(p => !p.site_type || p.site_type === 'geo');
      setPubs(geo);
      if (geo.length > 0 && !selectedPub) setSelectedPub(geo[0].id);
    } catch { toast.error('Failed to load publications'); }
  };

  const fetchLocations = async (pubId) => {
    if (!pubId) return;
    setLoading(true);
    try {
      const res = await api.get('/locations', { params: { publication_id: pubId } });
      setLocations(res.data || []);
    } catch { toast.error('Failed to load locations'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPubs(); }, []);
  useEffect(() => { if (selectedPub) fetchLocations(selectedPub); }, [selectedPub]);

  // Group by hub_name
  const grouped = locations.reduce((acc, loc) => {
    const hub = loc.hub_name || 'Ungrouped';
    if (!acc[hub]) acc[hub] = [];
    acc[hub].push(loc);
    return acc;
  }, {});

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, publication_id: selectedPub });
    setDialog(true);
  };

  const openEdit = (loc) => {
    setEditing(loc);
    setForm({
      hub_name:       loc.hub_name   || '',
      area_name:      loc.area_name  || '',
      aliases:        (loc.aliases || []).join(', '),
      region:         loc.region     || '',
      publication_id: selectedPub,
    });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.area_name.trim()) { toast.error('Area name is required'); return; }
    if (!form.hub_name.trim())  { toast.error('Hub name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        aliases: form.aliases.split(',').map(a => a.trim()).filter(Boolean),
      };
      if (editing) {
        await api.put(`/locations/${editing.id}`, payload);
        toast.success('Location updated');
      } else {
        await api.post('/locations', payload);
        toast.success('Location created');
      }
      setDialog(false);
      fetchLocations(selectedPub);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (locId, areaName) => {
    if (!window.confirm(`Delete "${areaName}"? Stories tagged to this area will lose this tag.`)) return;
    try {
      await api.delete(`/locations/${locId}`);
      toast.success('Location deleted');
      fetchLocations(selectedPub);
    } catch { toast.error('Failed to delete location'); }
  };

  const toggleHub = (hub) => setExpanded(prev => ({ ...prev, [hub]: !prev[hub] }));

  const label = 'text-xs text-[#6E6258] font-medium mb-1 block';
  const selectedPubObj = pubs.find(p => p.id === selectedPub);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-outfit tracking-tight">Local Areas</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage surrounding areas for each geo publication.</p>
        </div>
        {canManage && selectedPub && (
          <Button onClick={openCreate} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
            <Plus size={16} className="mr-1.5" /> Add Area
          </Button>
        )}
      </div>

      {/* Publication selector */}
      <div className="max-w-xs">
        <label className="text-sm text-[#6E6258] font-medium mb-1.5 block">Publication</label>
        <Select value={selectedPub} onValueChange={setSelectedPub}>
          <SelectTrigger className="bg-white text-[#1F1A17] border-border">
            <SelectValue placeholder="Select a geo publication..." />
          </SelectTrigger>
          <SelectContent className="bg-white border-border">
            {pubs.map(p => (
              <SelectItem key={p.id} value={p.id} className="text-[#1F1A17]">
                {p.display_name || p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedPub ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <MapPin className="mx-auto text-[#E7D2B8] mb-3" size={40} />
            <p className="text-muted-foreground">Select a publication to manage its local areas.</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 size={16} className="animate-spin" /> Loading areas...
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <MapPin className="mx-auto text-[#E7D2B8] mb-3" size={40} />
            <p className="text-muted-foreground text-lg">No areas yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add surrounding areas for {selectedPubObj?.display_name || selectedPubObj?.name}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([hub, areas]) => (
            <Card key={hub} className="bg-card border-border">
              <button
                onClick={() => toggleHub(hub)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#FFF4E8] transition-colors rounded-t-xl"
              >
                <div className="flex items-center gap-2">
                  <MapPin size={15} className="text-[#C9B49A]" />
                  <span className="font-medium text-foreground text-sm">{hub}</span>
                  <Badge className="bg-[#F8EBD9] text-[#6E6258] border-[#E7D2B8] text-xs">
                    {areas.length} area{areas.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                {expanded[hub]
                  ? <ChevronDown size={16} className="text-muted-foreground" />
                  : <ChevronRight size={16} className="text-muted-foreground" />
                }
              </button>

              {expanded[hub] && (
                <CardContent className="pt-0 pb-3 px-5">
                  <div className="space-y-1.5 mt-2">
                    {areas.sort((a, b) => a.area_name.localeCompare(b.area_name)).map(loc => (
                      <div
                        key={loc.id}
                        className="flex items-center justify-between px-3 py-2 bg-[#FFF4E8] rounded-lg border border-[#E7D2B8]"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">{loc.area_name}</p>
                          {loc.aliases?.length > 0 && (
                            <p className="text-xs text-muted-foreground truncate">
                              aka {loc.aliases.join(', ')}
                            </p>
                          )}
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-1 shrink-0 ml-2">
                            <Button variant="ghost" size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-blue-600 hover:bg-white"
                              onClick={() => openEdit(loc)}>
                              <Pencil size={13} />
                            </Button>
                            <Button variant="ghost" size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-red-500 hover:bg-red-50"
                              onClick={() => handleDelete(loc.id, loc.area_name)}>
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-outfit">
              {editing ? 'Edit Area' : 'Add Local Area'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {editing
                ? 'Update the area name, hub, or aliases.'
                : 'Add a surrounding area for this publication\'s Local Areas dropdown.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className={label}>Hub Name <span className="text-red-400">*</span></label>
              <Input value={form.hub_name} onChange={e => setForm(f => ({ ...f, hub_name: e.target.value }))}
                placeholder="e.g. Reading"
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
              <p className="text-xs text-muted-foreground mt-1">The primary city this publication is based around.</p>
            </div>
            <div>
              <label className={label}>Area Name <span className="text-red-400">*</span></label>
              <Input value={form.area_name} onChange={e => setForm(f => ({ ...f, area_name: e.target.value }))}
                placeholder="e.g. Wokingham"
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
            </div>
            <div>
              <label className={label}>Aliases <span className="text-[#9C8E84]">(comma separated)</span></label>
              <Input value={form.aliases} onChange={e => setForm(f => ({ ...f, aliases: e.target.value }))}
                placeholder="e.g. Wokingham Borough, WBC"
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
              <p className="text-xs text-muted-foreground mt-1">Alternative names used in story matching.</p>
            </div>
            <div>
              <label className={label}>Region</label>
              <Input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                placeholder="e.g. South East"
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-muted-foreground hover:text-foreground">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
              {saving ? <><Loader2 size={14} className="animate-spin mr-1.5" />Saving...</> : editing ? 'Save Changes' : 'Add Area'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}